import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Clock, Building2, PhoneCall, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { COMPANY, SERVICE_OPTIONS, BUDGET_OPTIONS } from "@/data/content";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  budget: "",
  message: "",
};

export const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      errs.name = "Please enter your full name.";
    if (!EMAIL_RE.test(form.email.trim()))
      errs.email = "Please enter a valid email address.";
    if (!form.service) errs.service = "Please select the service you're interested in.";
    if (!form.message.trim() || form.message.trim().length < 10)
      errs.message = "Please describe your project (at least 10 characters).";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service,
        budget: form.budget,
        message: form.message.trim(),
      });
      setSuccess(true);
      setForm(initialForm);
      toast.success("Message sent successfully", {
        description: "We will respond within 24 hours.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      toast.error("Could not send your message", {
        description: typeof detail === "string" ? detail : "Please check your details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-16 sm:py-20 lg:py-24 bg-card/40 border-t border-border"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Contact Us"
          title="Tell us what your business needs"
          description="This form is the single, direct line to the director. Describe your business and your goal — you'll have a clear answer within 24 hours."
        />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="card-highlight rounded-2xl bg-card border border-primary/30 p-10 text-center"
                data-testid="contact-success-banner"
              >
                <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-5" />
                <h3 className="text-2xl font-semibold text-foreground">Message received</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed max-w-md mx-auto">
                  Thank you for reaching out. Your enquiry has been delivered directly to the
                  director and you will receive a response within 24 hours.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSuccess(false)}
                  className="mt-7 rounded-xl border-border hover:bg-secondary transition-colors duration-200"
                  data-testid="contact-send-another-button"
                >
                  Send Another Message
                </Button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="card-highlight rounded-2xl bg-card border border-border p-6 sm:p-8 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="text-xs uppercase tracking-widest text-muted-foreground">
                      Full Name *
                    </Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="John Smith"
                      className="bg-secondary/50 border-border h-11 rounded-xl focus-visible:ring-primary"
                      data-testid="contact-form-name-input"
                    />
                    {errors.name && (
                      <p className="text-xs text-destructive" aria-live="polite" data-testid="form-error-text">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-xs uppercase tracking-widest text-muted-foreground">
                      Email Address *
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="you@company.com"
                      className="bg-secondary/50 border-border h-11 rounded-xl focus-visible:ring-primary"
                      data-testid="contact-form-email-input"
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive" aria-live="polite" data-testid="form-error-text">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone" className="text-xs uppercase tracking-widest text-muted-foreground">
                      Phone (optional)
                    </Label>
                    <Input
                      id="contact-phone"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="07123 456789"
                      className="bg-secondary/50 border-border h-11 rounded-xl focus-visible:ring-primary"
                      data-testid="contact-form-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Budget Range (optional)
                    </Label>
                    <Select value={form.budget} onValueChange={(v) => setField("budget", v)}>
                      <SelectTrigger
                        className="bg-secondary/50 border-border h-11 rounded-xl"
                        data-testid="contact-form-budget-select"
                      >
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        {BUDGET_OPTIONS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Service Interested In *
                  </Label>
                  <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                    <SelectTrigger
                      className="bg-secondary/50 border-border h-11 rounded-xl"
                      data-testid="contact-form-service-select"
                    >
                      <SelectValue placeholder="What do you need?" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border max-h-72">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.service && (
                    <p className="text-xs text-destructive" aria-live="polite" data-testid="form-error-text">
                      {errors.service}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-xs uppercase tracking-widest text-muted-foreground">
                    Your Message *
                  </Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    placeholder="Describe your business and what you need — a website, an app, an e-commerce store, a custom system..."
                    rows={5}
                    className="bg-secondary/50 border-border rounded-xl resize-none focus-visible:ring-primary"
                    data-testid="contact-form-message-textarea"
                  />
                  {errors.message && (
                    <p className="text-xs text-destructive" aria-live="polite" data-testid="form-error-text">
                      {errors.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="cta-shimmer w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl px-8 h-12 shadow-[0_10px_40px_rgba(214,179,106,0.25)] disabled:opacity-60 hover:-translate-y-px transition-transform duration-200"
                  data-testid="contact-form-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 space-y-5">
            <div className="card-highlight rounded-2xl bg-card border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-semibold uppercase tracking-widest text-foreground">
                  Response Promise
                </h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every enquiry is read personally by the director and answered within{" "}
                <span className="text-primary font-semibold">24 hours</span> — usually much
                sooner. No call centres, no ticket queues.
              </p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Websites & apps live in <span className="text-primary font-semibold">7 days max</span>{" "}
                — domain, hosting, SSL and database all included in your quote.
              </p>
            </div>

            <div className="card-highlight rounded-2xl bg-card border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-semibold uppercase tracking-widest text-foreground">
                  Company Details
                </h4>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground/70">Registered Name</span>
                  {COMPANY.name}
                </li>
                <li>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground/70">Company Number</span>
                  <span className="font-mono-meta">{COMPANY.number}</span>
                </li>
                <li>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground/70">Registered Office</span>
                  {COMPANY.address}
                </li>
              </ul>
            </div>

            <div className="card-highlight rounded-2xl bg-card border border-border p-6">
              <h4 className="text-sm font-semibold uppercase tracking-widest text-foreground mb-4">
                Direct Details
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-3">
                  <PhoneCall className="w-4 h-4 text-primary shrink-0" />
                  {COMPANY.phone}
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  {COMPANY.email}
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  {COMPANY.email2}
                </li>
              </ul>
              <p className="mt-4 text-xs text-muted-foreground/70 leading-relaxed">
                For fastest response, please use the contact form — it goes straight to the
                director's inbox.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
